import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createPostingRule,
  deletePostingRule,
  fetchPostingRules,
  fetchPostingRulesBanks,
  GLOBAL_BANK_CODE,
  GLOBAL_BANK_URL_TOKEN,
  reorderPostingRules,
  updatePostingRule,
  type OpenItemsSource,
  type PostingRule,
  type PostingRuleAction,
  type PostingRuleBank,
  type PostingRuleInput,
  type PostingRuleSign,
} from "../api";

/** Traduce el segmento de URL a bankCode real. `__all__` (o "*" URL-encoded)
 *  → "*". Cualquier otro string → tal cual. */
function bankCodeFromUrl(seg: string | undefined): string {
  if (!seg) return "";
  if (seg === GLOBAL_BANK_URL_TOKEN || seg === GLOBAL_BANK_CODE) return GLOBAL_BANK_CODE;
  return seg;
}

function bankCodeToUrl(bankCode: string): string {
  if (bankCode === GLOBAL_BANK_CODE) return GLOBAL_BANK_URL_TOKEN;
  return encodeURIComponent(bankCode);
}

function bankLabel(bankCode: string, bankName?: string): string {
  if (bankCode === GLOBAL_BANK_CODE) return "Reglas globales (todos los bancos)";
  return bankName || bankCode;
}

/**
 * Gestión de reglas de contabilización por banco.
 *   - `/reglas-contabilizacion`               → índice de bancos + nº reglas.
 *   - `/reglas-contabilizacion/:bankCode`     → CRUD del catálogo de ese banco.
 *
 * Tipos de acción:
 *   - fixed_account: contabiliza contra una cuenta contable fija (gastos, comisiones…).
 *   - open_items:    consulta facturas abiertas del CardCode indicado y ofrece
 *                    candidatos al usuario para saldar con IncomingPayment / VendorPayment.
 *   - bills_of_exchange: efectos (pendiente confirmar entidad concreta).
 */
export function PostingRulesView() {
  const { bankCode: bankCodeRaw } = useParams<{ bankCode?: string }>();
  const bankCode = bankCodeFromUrl(bankCodeRaw);
  return bankCode ? <PostingRulesBank bankCode={bankCode} /> : <PostingRulesIndex />;
}

// -----------------------------------------------------------------------------
// Índice de bancos
// -----------------------------------------------------------------------------

function PostingRulesIndex() {
  const [items, setItems] = useState<PostingRuleBank[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const r = await fetchPostingRulesBanks();
      setItems(r.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Reglas de contabilización</h2>
          <p className="meta">
            Por banco: patrón en descripción → acción (contrapartida fija,
            buscar factura abierta, efecto). Se aplican al contabilizar
            movimientos bancarios pendientes.
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}>
          {loading ? "Cargando…" : "Recargar"}
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {items === null && !error && <p>Cargando…</p>}

      {items && items.length === 0 && (
        <p className="meta">
          Todavía no hay bancos con cuentas dadas de alta. Añade cuentas desde
          "Cuentas" y vuelve aquí para configurar reglas.
        </p>
      )}

      {items && items.length > 0 && (
        <>
          <p className="meta" style={{ marginTop: 0 }}>
            💡 <strong>Reglas globales</strong> aplican a TODOS los bancos.
            Úsalas para conceptos transversales (comisiones, intereses,
            impuestos). Las reglas específicas de un banco tienen prioridad
            sobre las globales.
          </p>
          <table className="data">
            <thead>
              <tr>
                <th>Banco</th>
                <th className="num">Cuentas</th>
                <th className="num">Reglas</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const isGlobal = b.bankCode === GLOBAL_BANK_CODE;
                return (
                  <tr key={b.bankCode} style={isGlobal ? { background: "rgba(80,160,255,0.08)" } : undefined}>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {isGlobal ? "🌐 " : ""}
                        {bankLabel(b.bankCode, b.bankName)}
                      </div>
                      {!isGlobal && (
                        <div className="meta" style={{ fontSize: "0.85em" }}>
                          <code>{b.bankCode}</code>
                        </div>
                      )}
                    </td>
                    <td className="num">
                      {isGlobal ? <span className="meta">—</span> : b.accountsCount}
                    </td>
                    <td className="num">{b.rulesCount}</td>
                    <td>
                      <Link to={`/reglas-contabilizacion/${bankCodeToUrl(b.bankCode)}`}>
                        Gestionar reglas →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Detalle: reglas de un banco
// -----------------------------------------------------------------------------

type ActionKind =
  | "fixed_account"
  | "open_items"
  | "bills_of_exchange"
  | "leasing_installment"
  | "loan_installment";

type FormState = {
  name: string;
  /** Texto crudo del textarea: una línea por patrón. Al enviar se separa por
   *  saltos de línea. */
  patternsText: string;
  signFilter: PostingRuleSign;
  amountMin: string;
  amountMax: string;
  actionType: ActionKind;
  // fixed_account
  contraAccount: string;
  costingCode: string;
  projectCode: string;
  // open_items / bills_of_exchange
  dateWindowDays: string; // "" = sin límite
  // open_items sources
  sourceInvoices: boolean;
  sourcePurchase: boolean;
  // bills_of_exchange
  accountPayable: string;
  accountReceivable: string;
  // Extensión leasings
  matchLeasingByContract: boolean;
  // Extensión préstamos
  matchLoanByContract: boolean;
  matchAvalByLine: boolean;
};

const EMPTY_FORM: FormState = {
  name: "",
  patternsText: "",
  signFilter: "any",
  amountMin: "",
  amountMax: "",
  actionType: "fixed_account",
  contraAccount: "",
  costingCode: "",
  projectCode: "",
  dateWindowDays: "",
  sourceInvoices: false,
  sourcePurchase: false,
  accountPayable: "411000",
  accountReceivable: "431000",
  matchLeasingByContract: false,
  matchLoanByContract: false,
  matchAvalByLine: false,
};

/** Divide el textarea por saltos de línea; cada línea que no esté vacía es un
 *  patrón. Deduplicamos manteniendo el orden original. */
function parsePatternsText(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const s = raw.trim();
    if (!s) continue;
    const k = s.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out;
}

function parseNumOrNull(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function actionFromForm(f: FormState): PostingRuleAction {
  if (f.actionType === "fixed_account") {
    const cc = f.costingCode.trim();
    const pj = f.projectCode.trim();
    return {
      type: "fixed_account",
      contraAccount: f.contraAccount.trim(),
      ...(cc ? { costingCode: cc } : {}),
      ...(pj ? { projectCode: pj } : {}),
    };
  }
  if (f.actionType === "leasing_installment") {
    return { type: "leasing_installment" };
  }
  if (f.actionType === "loan_installment") {
    return { type: "loan_installment" };
  }
  const dateWindowDays = parseNumOrNull(f.dateWindowDays);
  if (f.actionType === "open_items") {
    const sources: OpenItemsSource[] = [];
    if (f.sourceInvoices) sources.push("invoices");
    if (f.sourcePurchase) sources.push("purchase_invoices");
    return {
      type: "open_items",
      sources,
      dateWindowDays,
    };
  }
  return {
    type: "bills_of_exchange",
    dateWindowDays,
    accountPayable: f.accountPayable.trim() || "411000",
    accountReceivable: f.accountReceivable.trim() || "431000",
  };
}

function toInput(f: FormState): PostingRuleInput {
  return {
    name: f.name,
    patterns: parsePatternsText(f.patternsText),
    signFilter: f.signFilter,
    amountMin: parseNumOrNull(f.amountMin),
    amountMax: parseNumOrNull(f.amountMax),
    action: actionFromForm(f),
    matchLeasingByContract: f.matchLeasingByContract,
    matchLoanByContract: f.matchLoanByContract,
    matchAvalByLine: f.matchAvalByLine,
  };
}

function ruleToForm(r: PostingRule): FormState {
  const base: FormState = {
    ...EMPTY_FORM,
    name: r.name,
    patternsText: r.patterns.join("\n"),
    signFilter: r.signFilter,
    amountMin: r.amountMin === null ? "" : String(r.amountMin),
    amountMax: r.amountMax === null ? "" : String(r.amountMax),
    actionType: r.action.type,
    matchLeasingByContract: r.matchLeasingByContract === true,
    matchLoanByContract: r.matchLoanByContract === true,
    matchAvalByLine: r.matchAvalByLine === true,
  };
  if (r.action.type === "fixed_account") {
    base.contraAccount = r.action.contraAccount;
    base.costingCode = r.action.costingCode ?? "";
    base.projectCode = r.action.projectCode ?? "";
  } else if (r.action.type === "open_items") {
    base.dateWindowDays = r.action.dateWindowDays === null ? "" : String(r.action.dateWindowDays);
    base.sourceInvoices = r.action.sources.includes("invoices");
    base.sourcePurchase = r.action.sources.includes("purchase_invoices");
  } else if (r.action.type === "bills_of_exchange") {
    base.dateWindowDays = r.action.dateWindowDays === null ? "" : String(r.action.dateWindowDays);
    base.accountPayable = r.action.accountPayable;
    base.accountReceivable = r.action.accountReceivable;
  }
  // leasing_installment: sin parámetros extra, todo se lee del leasing.
  return base;
}

function actionLabel(a: PostingRuleAction): string {
  if (a.type === "fixed_account") return `Fija · ${a.contraAccount}`;
  if (a.type === "open_items") {
    const sources = a.sources.length > 0 ? a.sources.join(",") : "auto";
    const win = a.dateWindowDays === null ? "sin límite" : `±${a.dateWindowDays}d`;
    return `Partidas abiertas · ${sources} · ${win}`;
  }
  if (a.type === "leasing_installment") {
    return `Leasing (auto por contrato del leasing)`;
  }
  if (a.type === "loan_installment") {
    return `Préstamo (auto por descriptionPattern / contract)`;
  }
  const win = a.dateWindowDays === null ? "sin límite" : `±${a.dateWindowDays}d`;
  return `Efectos · pag:${a.accountPayable} · cob:${a.accountReceivable} · ${win}`;
}

function PostingRulesBank({ bankCode }: { bankCode: string }) {
  const [rules, setRules] = useState<PostingRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const [newForm, setNewForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const r = await fetchPostingRules(bankCode);
      setRules(r.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCode]);

  const doCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await createPostingRule(bankCode, toInput(newForm));
      setNewForm(EMPTY_FORM);
      await load();
      setMsg("Regla creada.");
    } catch (e: unknown) {
      setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (r: PostingRule) => {
    setEditingId(r.id);
    setEditForm(ruleToForm(r));
    setMsg(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const doUpdate = async () => {
    if (!editingId || busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await updatePostingRule(bankCode, editingId, toInput(editForm));
      await load();
      cancelEdit();
      setMsg("Regla actualizada.");
    } catch (e: unknown) {
      setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (r: PostingRule) => {
    if (busy) return;
    if (!confirm(`¿Eliminar la regla "${r.name || r.patterns.join(" | ")}"?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await deletePostingRule(bankCode, r.id);
      await load();
      setMsg("Regla eliminada.");
    } catch (e: unknown) {
      setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const doMove = async (r: PostingRule, direction: -1 | 1) => {
    if (!rules || busy) return;
    const idx = rules.findIndex((x) => x.id === r.id);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= rules.length) return;
    const orderedIds = rules.map((x) => x.id);
    [orderedIds[idx], orderedIds[target]] = [orderedIds[target]!, orderedIds[idx]!];
    setBusy(true);
    try {
      const updated = await reorderPostingRules(bankCode, orderedIds);
      setRules(updated);
    } catch (e: unknown) {
      setMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const rulesEmpty = useMemo(() => rules !== null && rules.length === 0, [rules]);

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>
            Reglas · {bankCode === GLOBAL_BANK_CODE ? "🌐 Globales" : bankCode}
          </h2>
          <p className="meta">
            <Link to="/reglas-contabilizacion">← Bancos</Link>
            {"  ·  "}
            Se evalúan por orden. La primera regla en la que{" "}
            <strong>ALGUNO</strong> de sus patrones + signo + importe case,
            decide qué hacer al contabilizar.
            {bankCode === GLOBAL_BANK_CODE ? (
              <> Estas reglas <strong>aplican a TODOS los bancos</strong> como fallback cuando el banco no tiene regla propia que case.</>
            ) : (
              <> Las reglas específicas de este banco tienen prioridad sobre las <Link to={`/reglas-contabilizacion/${GLOBAL_BANK_URL_TOKEN}`}>globales</Link>.</>
            )}
          </p>
        </div>
        <button type="button" onClick={load} disabled={loading}>
          {loading ? "Cargando…" : "Recargar"}
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {msg && <p className={msg.startsWith("❌") ? "error" : "meta"}>{msg}</p>}

      {rules && (
        <>
          {rulesEmpty ? (
            <p className="meta" style={{ marginTop: 12 }}>
              Aún no hay reglas para este banco. Usa el formulario de abajo para crear la primera.
            </p>
          ) : (
            <table className="data" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th>Nombre</th>
                  <th>Patrón</th>
                  <th>Signo</th>
                  <th className="num">Imp. mín.</th>
                  <th className="num">Imp. máx.</th>
                  <th>Acción</th>
                  <th style={{ width: 160 }}></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r, i) => {
                  const isEditing = editingId === r.id;
                  return isEditing ? (
                    <tr key={r.id}>
                      <td colSpan={8}>
                        <RuleForm value={editForm} onChange={setEditForm} />
                        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                          <button type="button" onClick={doUpdate} disabled={busy}>
                            Guardar
                          </button>
                          <button type="button" onClick={cancelEdit} disabled={busy}>
                            Cancelar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td>{r.name || <span className="meta">—</span>}</td>
                      <td>
                        {r.patterns.length === 1 ? (
                          <code>{r.patterns[0]}</code>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {r.patterns.map((p) => (
                              <code
                                key={p}
                                style={{
                                  padding: "0 0.35rem",
                                  background: "rgba(127,127,127,0.12)",
                                  borderRadius: 3,
                                }}
                              >
                                {p}
                              </code>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>{signLabel(r.signFilter)}</td>
                      <td className="num">
                        {r.amountMin === null ? <span className="meta">—</span> : formatNum(r.amountMin)}
                      </td>
                      <td className="num">
                        {r.amountMax === null ? <span className="meta">—</span> : formatNum(r.amountMax)}
                      </td>
                      <td>
                        <span title={JSON.stringify(r.action)}>{actionLabel(r.action)}</span>
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            type="button"
                            onClick={() => doMove(r, -1)}
                            disabled={busy || i === 0}
                            title="Subir prioridad"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => doMove(r, +1)}
                            disabled={busy || i === rules.length - 1}
                            title="Bajar prioridad"
                          >
                            ↓
                          </button>
                          <button type="button" onClick={() => startEdit(r)} disabled={busy}>
                            Editar
                          </button>
                          <button type="button" onClick={() => doDelete(r)} disabled={busy}>
                            Borrar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <h3 style={{ marginTop: 24 }}>Nueva regla</h3>
          <form onSubmit={doCreate}>
            <RuleForm value={newForm} onChange={setNewForm} />
            <div style={{ marginTop: 12 }}>
              <button type="submit" disabled={busy}>
                {busy ? "Guardando…" : "Añadir regla"}
              </button>
            </div>
          </form>
        </>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// Formulario reutilizable (crear / editar)
// -----------------------------------------------------------------------------

function RuleForm({
  value,
  onChange,
}: {
  value: FormState;
  onChange: (f: FormState) => void;
}) {
  const setF = (patch: Partial<FormState>) => onChange({ ...value, ...patch });
  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
          gap: 8,
          alignItems: "end",
        }}
      >
        <label>
          <div className="meta">Nombre (opcional)</div>
          <input
            type="text"
            value={value.name}
            onChange={(e) => setF({ name: e.target.value })}
            placeholder="Comisión mantenimiento"
          />
        </label>
        <label style={{ gridColumn: "span 2" }}>
          <div className="meta">
            Patrones — <strong>una línea por variante</strong> (OR); vale con
            que casen substring case-insensitive
          </div>
          <textarea
            required
            rows={3}
            value={value.patternsText}
            onChange={(e) => setF({ patternsText: e.target.value })}
            placeholder={"COMISION\nCOMISIÓN\nCOM. MANT"}
            style={{ width: "100%", fontFamily: "monospace", resize: "vertical" }}
          />
        </label>
        <label>
          <div className="meta">Signo</div>
          <select
            value={value.signFilter}
            onChange={(e) => setF({ signFilter: e.target.value as PostingRuleSign })}
          >
            <option value="any">Cualquiera</option>
            <option value="debit">Solo cargos (–)</option>
            <option value="credit">Solo abonos (+)</option>
          </select>
        </label>
        <label>
          <div className="meta">Importe mín. (|abs|)</div>
          <input
            type="text"
            inputMode="decimal"
            value={value.amountMin}
            onChange={(e) => setF({ amountMin: e.target.value })}
            placeholder="opcional"
          />
        </label>
        <label>
          <div className="meta">Importe máx. (|abs|)</div>
          <input
            type="text"
            inputMode="decimal"
            value={value.amountMax}
            onChange={(e) => setF({ amountMax: e.target.value })}
            placeholder="opcional"
          />
        </label>
        <label>
          <div className="meta">Acción</div>
          <select
            value={value.actionType}
            onChange={(e) => setF({ actionType: e.target.value as ActionKind })}
          >
            <option value="fixed_account">Contrapartida fija</option>
            <option value="open_items">Buscar en partidas abiertas</option>
            <option value="bills_of_exchange">Buscar en efectos</option>
            <option value="leasing_installment">Contabilizar cuota de leasing</option>
            <option value="loan_installment">Contabilizar cuota de préstamo</option>
          </select>
        </label>
      </div>

      {/* Extensión leasings — flag opcional */}
      <div style={{ marginTop: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.9em" }}>
          <input
            type="checkbox"
            checked={value.matchLeasingByContract}
            onChange={(e) => setF({ matchLeasingByContract: e.target.checked })}
          />
          <span>
            Detectar también leasings por los últimos 10 dígitos del contrato
            <span className="meta" style={{ fontSize: "0.8em", marginLeft: 6 }}>
              (para cubrir constitución/apertura además de las cuotas normales)
            </span>
          </span>
        </label>
      </div>

      {/* Extensión préstamos — flag opcional */}
      <div style={{ marginTop: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.9em" }}>
          <input
            type="checkbox"
            checked={value.matchLoanByContract}
            onChange={(e) => setF({ matchLoanByContract: e.target.checked })}
          />
          <span>
            Detectar también préstamos por descriptionPattern o nº de contrato
            <span className="meta" style={{ fontSize: "0.8em", marginLeft: 6 }}>
              (combina naturalmente con acción "Contabilizar cuota de préstamo")
            </span>
          </span>
        </label>
      </div>

      {/* Extensión avales — flag opcional. Combinado con fixed_account:
          si el memo contiene el nº de aval o nº de línea, la cuenta contable
          y CC/PJ del asiento se sobrescriben con los del aval detectado. */}
      <div style={{ marginTop: 8 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.9em" }}>
          <input
            type="checkbox"
            checked={value.matchAvalByLine}
            onChange={(e) => setF({ matchAvalByLine: e.target.checked })}
          />
          <span>
            Detectar también avales por el nº de línea o nº de aval en el memo
            <span className="meta" style={{ fontSize: "0.8em", marginLeft: 6 }}>
              (usa la cuenta de gasto de la línea y CECO/proyecto del aval)
            </span>
          </span>
        </label>
      </div>

      {/* Bloque específico por tipo de acción */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: 8,
          alignItems: "end",
          marginTop: 12,
          padding: 12,
          background: "rgba(127,127,127,0.06)",
          borderRadius: 6,
        }}
      >
        {value.actionType === "fixed_account" && (
          <>
            <label>
              <div className="meta">
                Cuenta contrapartida SAP
                {value.matchAvalByLine && (
                  <span
                    className="meta"
                    style={{ marginLeft: 4, fontSize: "0.85em" }}
                    title="Con detección de avales activa, la cuenta la aporta la línea del aval detectado. Déjalo vacío si no quieres fallback."
                  >
                    · opcional (la aporta el aval detectado)
                  </span>
                )}
              </div>
              <input
                type="text"
                required={!value.matchAvalByLine}
                value={value.contraAccount}
                onChange={(e) => setF({ contraAccount: e.target.value })}
                placeholder={value.matchAvalByLine ? "opcional — solo fallback" : "626000000000"}
              />
            </label>
            <label>
              <div className="meta">
                Centro de coste (OPRC)
                <span
                  className="meta"
                  style={{ marginLeft: 4, fontSize: "0.85em" }}
                  title="Opcional en la regla. Si la cuenta empieza por 6 o 7, se te pedirá al contabilizar el movimiento."
                >
                  · opcional (se pedirá al contabilizar si hace falta)
                </span>
              </div>
              <input
                type="text"
                value={value.costingCode}
                onChange={(e) => setF({ costingCode: e.target.value })}
                placeholder="opcional"
              />
            </label>
            <label>
              <div className="meta">
                Proyecto (OPRJ)
                <span
                  className="meta"
                  style={{ marginLeft: 4, fontSize: "0.85em" }}
                  title="Opcional en la regla. Si la cuenta empieza por 6 o 7, se te pedirá al contabilizar el movimiento junto al centro de coste."
                >
                  · opcional (se pedirá al contabilizar si hace falta)
                </span>
              </div>
              <input
                type="text"
                value={value.projectCode}
                onChange={(e) => setF({ projectCode: e.target.value })}
                placeholder="opcional"
              />
            </label>
          </>
        )}
        {(value.actionType === "open_items" || value.actionType === "bills_of_exchange") && (
          <>
            <label>
              <div className="meta">Ventana ±días (vacío = sin límite)</div>
              <input
                type="text"
                inputMode="numeric"
                value={value.dateWindowDays}
                onChange={(e) => setF({ dateWindowDays: e.target.value })}
                placeholder="30"
              />
            </label>
            <p className="meta" style={{ gridColumn: "1 / -1", margin: 0 }}>
              La búsqueda se hace automática: extraemos importe, nombre del
              ordenante y números tipo factura del texto del movimiento y
              proponemos candidatos abiertos en SAP.
            </p>
          </>
        )}
        {value.actionType === "open_items" && (
          <div>
            <div className="meta">Fuentes (vacío = auto por signo)</div>
            <label style={{ display: "block" }}>
              <input
                type="checkbox"
                checked={value.sourceInvoices}
                onChange={(e) => setF({ sourceInvoices: e.target.checked })}
              />
              {" "}Facturas cliente (Invoices)
            </label>
            <label style={{ display: "block" }}>
              <input
                type="checkbox"
                checked={value.sourcePurchase}
                onChange={(e) => setF({ sourcePurchase: e.target.checked })}
              />
              {" "}Facturas proveedor (PurchaseInvoices)
            </label>
          </div>
        )}
        {value.actionType === "bills_of_exchange" && (
          <>
            <label>
              <div className="meta">Cuenta SAP · efectos a PAGAR</div>
              <input
                type="text"
                value={value.accountPayable}
                onChange={(e) => setF({ accountPayable: e.target.value })}
                placeholder="411000"
              />
            </label>
            <label>
              <div className="meta">Cuenta SAP · efectos a COBRAR</div>
              <input
                type="text"
                value={value.accountReceivable}
                onChange={(e) => setF({ accountReceivable: e.target.value })}
                placeholder="431000"
              />
            </label>
            <p className="meta" style={{ gridColumn: "1 / -1", margin: 0 }}>
              Se consulta BillOfExchangeTransactions (tabla OBOE) por importe,
              nombre del librador y número extraído del memo. Al contabilizar
              se crea un JournalEntry banco vs cuenta de efectos.
            </p>
          </>
        )}
        {value.actionType === "leasing_installment" && (
          <p className="meta" style={{ gridColumn: "1 / -1", margin: 0 }}>
            Detecta el leasing por los últimos 10 dígitos del contrato en el
            memo, busca la cuota que casa por fecha ±7d + importe ±1€, y
            genera: (1) PurchaseInvoice del proveedor con Amortizado→CP,
            Intereses→662 y Comisiones→626 usando las cuentas del leasing;
            (2) VendorPayment aplicando esa factura al banco; (3) JournalEntry
            de reclasificación LP→CP por la cuota N+12. Las constituciones
            (memo con "CONSTITUC"/"APERTUR") aún no están automatizadas.
          </p>
        )}
        {value.actionType === "loan_installment" && (
          <p className="meta" style={{ gridColumn: "1 / -1", margin: 0 }}>
            Detecta el préstamo por el <code>descriptionPattern</code> (o los
            últimos 10 dígitos del contractNumber) en el memo, localiza la
            cuota que casa por fecha ±7d + importe ±1€ dentro del cuadro
            (Wealth Reader), y genera un <strong>único</strong> JournalEntry
            con: amortización a CP, intereses, comisiones (si el cargo excede
            amortización+intereses), banco al haber, y reclasificación LP→CP
            de la cuota N+12 (si aplica).
          </p>
        )}
      </div>
    </>
  );
}

function signLabel(s: PostingRuleSign): string {
  if (s === "debit") return "Cargos (–)";
  if (s === "credit") return "Abonos (+)";
  return "Cualquiera";
}

function formatNum(n: number): string {
  return n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
